import React, { useState, useEffect } from 'react';
import BudgetTable from './BudgetTable';
import BudgetSummary from './BudgetSummary';
import BudgetChart from './BudgetChart';
import EducationSavings from '../EducationSavings/EducationSavings';
import { openDB } from 'idb';
import './AnnualBudget.css';

// indexedDBを初期化する関数
const initDB = async () => {
  const db = await openDB('annual-budget-db', 2, {
    upgrade(db, oldVersion) {
      // 予算データストア
      if (!db.objectStoreNames.contains('budgets')) {
        const store = db.createObjectStore('budgets', { keyPath: 'id', autoIncrement: true });
        store.createIndex('title', 'title', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
      
      // 教育費データストア（バージョン2で追加）
      if (oldVersion < 2 && !db.objectStoreNames.contains('education-savings')) {
        const educationStore = db.createObjectStore('education-savings', { keyPath: 'id', autoIncrement: true });
        educationStore.createIndex('budgetId', 'budgetId', { unique: false });
        educationStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    }
  });
  return db;
};

const AnnualBudget = () => {
  // 状態管理
  const [title, setTitle] = useState('年間収支');
  const [incomeItems, setIncomeItems] = useState([
    { id: 1, content: '給料', frequency: 'month', amount: 250000 }
  ]);
  const [expenseItems, setExpenseItems] = useState([
    { id: 1, content: '家賃', frequency: 'month', amount: 80000 },
    { id: 2, content: '食費', frequency: 'month', amount: 50000 }
  ]);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
  const [showCharts, setShowCharts] = useState(true);
  const [db, setDb] = useState(null);
  const [savedStatus, setSavedStatus] = useState('初回表示');
  const [currentBudgetId, setCurrentBudgetId] = useState(null);
  const [savedBudgets, setSavedBudgets] = useState([]);
  
  // タブ管理
  const [activeTab, setActiveTab] = useState('budget'); // 'budget' or 'education'
  
  // 教育費データ
  const [educationData, setEducationData] = useState({
    childAge: 5,
    schoolType: 'public',
    currentEducationSavings: 20000,
    hasGrandparents: false,
    totalEducationCost: 0
  });

  // indexedDBの初期化
  useEffect(() => {
    const setupDB = async () => {
      const database = await initDB();
      setDb(database);
      // 保存されている予算一覧を取得
      const tx = database.transaction('budgets', 'readonly');
      const store = tx.objectStore('budgets');
      const budgets = await store.getAll();
      setSavedBudgets(budgets);
      
      // 最新の予算があれば読み込む
      if (budgets.length > 0) {
        const latestBudget = budgets.reduce((latest, budget) => {
          return latest.updatedAt > budget.updatedAt ? latest : budget;
        });
        loadBudget(latestBudget);
      }
    };
    
    setupDB();
    
    // PWAのインストール判定
    window.addEventListener('beforeinstallprompt', (e) => {
      // インストールプロンプトを保存
      e.preventDefault();
      window.deferredPrompt = e;
    });
    
    // サービスワーカーの登録（ローカル開発環境以外で有効化）
    const isProduction = window.location.hostname !== 'localhost' && 
                        window.location.hostname !== '127.0.0.1';
    
    if ('serviceWorker' in navigator && isProduction) {
      navigator.serviceWorker.register('/annual-budget-sw.js', { scope: '/annual-budget' })
        .then(reg => console.log('Service worker registered:', reg))
        .catch(err => console.error('Service worker registration failed:', err));
    } else {
      console.log('Development mode: Service Worker disabled for AnnualBudget');
      
      // 開発環境で既存のService Workerを削除
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            if (registration.scope.includes('/annual-budget')) {
              registration.unregister();
              console.log('Unregistered Service Worker for annual-budget');
            }
          });
        });
      }
    }
  }, []);

  // データ変更時の自動保存
  useEffect(() => {
    if (db && (incomeItems.length > 0 || expenseItems.length > 0)) {
      const autoSave = setTimeout(async () => {
        await saveBudget();
        setSavedStatus('自動保存されました');
        setTimeout(() => setSavedStatus(''), 3000);
      }, 2000);
      
      return () => clearTimeout(autoSave);
    }
  }, [title, incomeItems, expenseItems, db]);
  
  // 教育費データの自動保存
  useEffect(() => {
    if (db && currentBudgetId && educationData.totalEducationCost > 0) {
      const autoSaveEducation = setTimeout(async () => {
        await saveEducationData();
      }, 2000);
      
      return () => clearTimeout(autoSaveEducation);
    }
  }, [educationData, db, currentBudgetId]);
  
  // 予算を保存する関数
  const saveBudget = async () => {
    if (!db) return;
    
    const budgetData = {
      title,
      incomeItems,
      expenseItems,
      updatedAt: new Date().toISOString()
    };
    
    try {
      const tx = db.transaction('budgets', 'readwrite');
      const store = tx.objectStore('budgets');
      
      // 新規作成または更新
      if (currentBudgetId) {
        budgetData.id = currentBudgetId;
        await store.put(budgetData);
      } else {
        const id = await store.add(budgetData);
        setCurrentBudgetId(id);
      }
      
      await tx.done;
      
      // 保存済み予算リストを更新
      const allBudgets = await db.getAll('budgets');
      setSavedBudgets(allBudgets);
      
      return true;
    } catch (error) {
      console.error('保存エラー:', error);
      return false;
    }
  };
  
  // CSVとしてエクスポートする関数
  const exportToCsv = () => {
    // ヘッダー
    let csvContent = "データ:,年間収支計算\n";
    csvContent += "タイトル:," + title + "\n";
    csvContent += "作成日:," + new Date().toLocaleDateString() + "\n\n";
    
    // 収入セクション
    csvContent += "収入\n";
    csvContent += "内容,頻度,金額\n";
    incomeItems.forEach(item => {
      csvContent += `${item.content},${getFrequencyLabel(item.frequency)},${item.amount}\n`;
    });
    
    // 支出セクション
    csvContent += "\n支出\n";
    csvContent += "内容,頻度,金額\n";
    expenseItems.forEach(item => {
      csvContent += `${item.content},${getFrequencyLabel(item.frequency)},${item.amount}\n`;
    });
    
    // 合計セクション
    const { monthlyIncome, yearlyIncome } = calculateTotals(incomeItems);
    const { monthlyIncome: monthlyExpense, yearlyIncome: yearlyExpense } = calculateTotals(expenseItems);
    
    csvContent += "\n合計\n";
    csvContent += `月間収入,${monthlyIncome}\n`;
    csvContent += `月間支出,${monthlyExpense}\n`;
    csvContent += `月間差額,${monthlyIncome - monthlyExpense}\n`;
    csvContent += `年間収入,${yearlyIncome}\n`;
    csvContent += `年間支出,${yearlyExpense}\n`;
    csvContent += `年間差額,${yearlyIncome - yearlyExpense}\n`;
    
    // CSVファイルとしてダウンロード
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${title}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSavedStatus('CSVファイルを保存しました');
    setTimeout(() => setSavedStatus(''), 3000);
  };
  
  // 予算を読み込む関数
  const loadBudget = async (budget) => {
    setTitle(budget.title);
    setIncomeItems(budget.incomeItems);
    setExpenseItems(budget.expenseItems);
    setCurrentBudgetId(budget.id);
    
    // 教育費データも読み込む
    await loadEducationData(budget.id);
    
    setSavedStatus('読み込みました');
    setTimeout(() => setSavedStatus(''), 3000);
  };
  
  // 頻度に応じた表示ラベルを取得
  const getFrequencyLabel = (frequency) => {
    const labels = {
      'day': '毎日',
      'week': '毎週',
      'month': '毎月',
      'year': '毎年',
      'biennial': '2年に1度'
    };
    return labels[frequency] || frequency;
  };
  
  // 合計金額を計算する関数
  const calculateTotals = (items) => {
    let monthlyTotal = 0;
    let yearlyTotal = 0;
    
    items.forEach(item => {
      const amount = parseFloat(item.amount) || 0;
      
      switch (item.frequency) {
        case 'day':
          monthlyTotal += amount * 30;
          yearlyTotal += amount * 365;
          break;
        case 'week':
          monthlyTotal += amount * 4.35;
          yearlyTotal += amount * 52;
          break;
        case 'month':
          monthlyTotal += amount;
          yearlyTotal += amount * 12;
          break;
        case 'year':
          monthlyTotal += amount / 12;
          yearlyTotal += amount;
          break;
        case 'biennial':
          monthlyTotal += amount / 24;
          yearlyTotal += amount / 2;
          break;
        default:
          break;
      }
    });
    
    return {
      monthlyIncome: Math.round(monthlyTotal),
      yearlyIncome: Math.round(yearlyTotal)
    };
  };
  
  // 収入項目の追加/編集/削除関数
  const addIncomeItem = () => {
    const newId = incomeItems.length > 0 
      ? Math.max(...incomeItems.map(item => item.id)) + 1 
      : 1;
    setIncomeItems([...incomeItems, { id: newId, content: '', frequency: 'month', amount: 0 }]);
  };
  
  const updateIncomeItem = (id, field, value) => {
    setIncomeItems(incomeItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  const deleteIncomeItem = (id) => {
    setIncomeItems(incomeItems.filter(item => item.id !== id));
  };
  
  // 支出項目の追加/編集/削除関数
  const addExpenseItem = () => {
    const newId = expenseItems.length > 0 
      ? Math.max(...expenseItems.map(item => item.id)) + 1 
      : 1;
    setExpenseItems([...expenseItems, { id: newId, content: '', frequency: 'month', amount: 0 }]);
  };
  
  const updateExpenseItem = (id, field, value) => {
    setExpenseItems(expenseItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  const deleteExpenseItem = (id) => {
    setExpenseItems(expenseItems.filter(item => item.id !== id));
  };
  
  // 新しい予算の作成
  const createNewBudget = () => {
    setTitle('年間収支');
    setIncomeItems([{ id: 1, content: '', frequency: 'month', amount: 0 }]);
    setExpenseItems([{ id: 1, content: '', frequency: 'month', amount: 0 }]);
    setCurrentBudgetId(null);
    setSavedStatus('新しい予算を作成しました');
    setTimeout(() => setSavedStatus(''), 3000);
  };

  // 使い方セクションへのスムーススクロール
  const scrollToUsageGuide = () => {
    const usageElement = document.getElementById('usage-guide');
    if (usageElement) {
      usageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  // 教育費データ変更ハンドラー
  const handleEducationDataChange = (newData) => {
    setEducationData(prev => ({ ...prev, ...newData }));
  };
  
  // 教育費データを保存する関数
  const saveEducationData = async () => {
    if (!db || !currentBudgetId) return;
    
    const educationSavingsData = {
      budgetId: currentBudgetId,
      ...educationData,
      updatedAt: new Date().toISOString()
    };
    
    try {
      const tx = db.transaction('education-savings', 'readwrite');
      const store = tx.objectStore('education-savings');
      
      // 既存データを検索
      const index = store.index('budgetId');
      const existingData = await index.get(currentBudgetId);
      
      if (existingData) {
        // 更新
        educationSavingsData.id = existingData.id;
        await store.put(educationSavingsData);
      } else {
        // 新規作成
        await store.add(educationSavingsData);
      }
      
      await tx.done;
      return true;
    } catch (error) {
      console.error('教育費データ保存エラー:', error);
      return false;
    }
  };
  
  // 教育費データを読み込む関数
  const loadEducationData = async (budgetId) => {
    if (!db) return;
    
    try {
      const tx = db.transaction('education-savings', 'readonly');
      const store = tx.objectStore('education-savings');
      const index = store.index('budgetId');
      const data = await index.get(budgetId);
      
      if (data) {
        setEducationData({
          childAge: data.childAge || 5,
          schoolType: data.schoolType || 'public',
          currentEducationSavings: data.currentEducationSavings || 20000,
          hasGrandparents: data.hasGrandparents || false,
          totalEducationCost: data.totalEducationCost || 0
        });
      }
    } catch (error) {
      console.error('教育費データ読み込みエラー:', error);
    }
  };

  // 合計を計算
  const { monthlyIncome, yearlyIncome } = calculateTotals(incomeItems);
  const { monthlyIncome: monthlyExpense, yearlyIncome: yearlyExpense } = calculateTotals(expenseItems);
  
  const monthlySavings = monthlyIncome - monthlyExpense;
  const yearlySavings = yearlyIncome - yearlyExpense;

  return (
    <div className="annual-budget">
      <div className="budget-header">
        <input
          type="text"
          className="budget-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="予算タイトル"
        />
        <div className="header-buttons">
          <button 
            className="help-button"
            onClick={scrollToUsageGuide}
            title="使い方を見る"
            aria-label="使い方セクションへ移動"
          >
            ?
          </button>
          <div className="save-status">{savedStatus}</div>
        </div>
      </div>
      
      {/* タブナビゲーション */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'budget' ? 'active' : ''}`}
          onClick={() => setActiveTab('budget')}
        >
          <span className="tab-icon">💰</span>
          <span className="tab-text">収支計算</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => setActiveTab('education')}
        >
          <span className="tab-icon">🎓</span>
          <span className="tab-text">教育費計画</span>
          <span className="new-badge" style={{ backgroundColor: '#e67e22' }}>BETA</span>
        </button>
      </div>
      
      {/* タブコンテンツ */}
      <div className="tab-content">
        {activeTab === 'budget' && (
          <div className="budget-tab">
            <div className="budget-actions">
        <button onClick={() => setShowCharts(!showCharts)}>
          {showCharts ? 'グラフを隠す' : 'グラフを表示'}
        </button>
        <button onClick={exportToCsv}>CSVで保存</button>
        <button onClick={createNewBudget}>新しい予算</button>
        
        <div className="view-toggle">
          <button 
            className={viewMode === 'monthly' ? 'active' : ''}
            onClick={() => setViewMode('monthly')}
          >
            月表示
          </button>
          <button 
            className={viewMode === 'yearly' ? 'active' : ''}
            onClick={() => setViewMode('yearly')}
          >
            年表示
          </button>
        </div>
      </div>
      
      {savedBudgets.length > 0 && (
        <div className="saved-budgets">
          <select 
            onChange={(e) => {
              const selectedId = parseInt(e.target.value);
              if (selectedId === -1) return;
              
              const selected = savedBudgets.find(b => b.id === selectedId);
              if (selected) loadBudget(selected);
            }}
            value={currentBudgetId || -1}
          >
            <option value="-1">保存済み予算を選択...</option>
            {savedBudgets.map(budget => (
              <option key={budget.id} value={budget.id}>
                {budget.title} ({new Date(budget.updatedAt).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      )}
      
      {showCharts && (
        <>
          <BudgetChart 
            incomeTotal={viewMode === 'monthly' ? monthlyIncome : yearlyIncome}
            expenseTotal={viewMode === 'monthly' ? monthlyExpense : yearlyExpense}
            viewMode={viewMode}
          />
          
          <BudgetSummary 
            monthlyIncome={monthlyIncome}
            monthlyExpense={monthlyExpense}
            monthlySavings={monthlySavings}
            yearlyIncome={yearlyIncome}
            yearlyExpense={yearlyExpense}
            yearlySavings={yearlySavings}
            viewMode={viewMode}
          />
        </>
      )}
      
      <div className="budget-tables">
        <div className="tables-container">
          <div className="table-section">
            <h3>収入</h3>
            <BudgetTable
              items={incomeItems}
              onAddItem={addIncomeItem}
              onUpdateItem={updateIncomeItem}
              onDeleteItem={deleteIncomeItem}
              type="income"
            />
          </div>
          
          <div className="table-section">
            <h3>支出</h3>
            <BudgetTable
              items={expenseItems}
              onAddItem={addExpenseItem}
              onUpdateItem={updateExpenseItem}
              onDeleteItem={deleteExpenseItem}
              type="expense"
            />
          </div>
        </div>
      </div>
      
            <div id="usage-guide" className="usage-guide">
              <h3>使い方</h3>
              <ul>
                <li>テーブルに直接入力して収入と支出を記録できます</li>
                <li>「+」をクリックして新しい項目を追加できます</li>
                <li>頻度（毎日/毎週/毎月/毎年/2年に1度）を選択できます</li>
                <li>データは自動的に保存されますが、CSVファイルとしても保存できます</li>
                <li>CSVファイルはiCloudやGoogleDriveで共有すれば、異なるデバイス間でデータを共有できます</li>
                <li>このページはオフラインでも使用できます（PWA機能）</li>
              </ul>
            </div>
          </div>
        )}
        
        {activeTab === 'education' && (
          <div className="education-tab">
            <EducationSavings
              monthlyIncome={monthlyIncome}
              monthlyExpense={monthlyExpense}
              currentSavings={707667} // 初期値として設定
              onDataChange={handleEducationDataChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnualBudget; 